import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Form, InputGroup, ProgressBar, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import Modal_proyectos from '../menus/Modal';
import {
  create_subsystem_version,
  delete_subsystem_version,
  get_subsystem_versions,
  update_subsystem_version,
} from '../../utils/services';

const DESCRIPTION_PREVIEW_MAX = 80;
const PACKAGE_TYPES = [
  { value: 'subsystem', label: 'Subsistema' },
  { value: 'tomi8_package', label: 'Paquete TOMi8' },
];

const EMPTY_FORM = {
  package_name: '',
  description: '',
  type: 'subsystem',
  file: null,
};

const SORTABLE_COLUMNS = {
  package_name: 'Paquete',
  type: 'Tipo',
  description: 'Descripción',
  publish_date: 'Fecha publicación',
  url: 'URL',
};

function truncateDescription(text, max = DESCRIPTION_PREVIEW_MAX) {
  const value = String(text ?? '');
  if (value.length <= max) {
    return value;
  }
  return `${value.slice(0, max)}…`;
}

function isValidArchiveFile(file) {
  if (!file) {
    return false;
  }
  const lower = file.name.toLowerCase();
  return lower.endsWith('.tar.gz') || lower.endsWith('.tgz');
}

function formatPublishDate(dateValue) {
  if (!dateValue) {
    return '';
  }
  const parsed = new Date(String(dateValue).replace(' ', 'T'));
  if (Number.isNaN(parsed.getTime())) {
    return dateValue;
  }
  return parsed.toLocaleString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatBytes(bytes) {
  if (!bytes || bytes <= 0) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  return `${value.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

function formatTypeLabel(type) {
  const found = PACKAGE_TYPES.find((item) => item.value === type);
  return found ? found.label : type || '-';
}

function getSortValue(version, column) {
  if (column === 'publish_date') {
    const parsed = new Date(String(version.publish_date ?? '').replace(' ', 'T'));
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
  }
  if (column === 'type') {
    return formatTypeLabel(version.type).toLowerCase();
  }
  return String(version[column] ?? '').toLowerCase();
}

function SubsistemaTOMi() {
  const token = localStorage.getItem('token');
  const navigate = useNavigate();

  const [versions, setVersions] = useState([]);
  const [nameFilter, setNameFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [sortColumn, setSortColumn] = useState('publish_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentVersionId, setCurrentVersionId] = useState(null);
  const [currentUrl, setCurrentUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [formValues, setFormValues] = useState(EMPTY_FORM);

  const fetchVersions = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await get_subsystem_versions(token);
      if (result === 'INVALID') {
        navigate('/');
        return;
      }
      if (result === null) {
        setVersions([]);
        return;
      }
      if (!Array.isArray(result)) {
        setVersions([]);
        setError('No se pudieron cargar las versiones del subsistema.');
        return;
      }
      setVersions(result);
    } catch (err) {
      setVersions([]);
      setError('Error al cargar las versiones del subsistema.');
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  const filteredVersions = useMemo(() => {
    const nameTerm = nameFilter.trim().toLowerCase();
    const filtered = versions.filter((version) => {
      const matchesName =
        !nameTerm ||
        String(version.package_name ?? '').toLowerCase().includes(nameTerm) ||
        String(version.description ?? '').toLowerCase().includes(nameTerm);
      const matchesType = !typeFilter || version.type === typeFilter;
      return matchesName && matchesType;
    });

    const sorted = [...filtered].sort((a, b) => {
      const valueA = getSortValue(a, sortColumn);
      const valueB = getSortValue(b, sortColumn);
      if (valueA < valueB) {
        return sortDirection === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return sortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sorted;
  }, [versions, nameFilter, typeFilter, sortColumn, sortDirection]);

  const resetForm = () => {
    setFormValues(EMPTY_FORM);
    setCurrentVersionId(null);
    setCurrentUrl('');
    setIsEditing(false);
    setUploadProgress(null);
  };

  const handleUploadProgress = (progress) => {
    setUploadProgress({
      loaded: progress.loaded,
      total: progress.total,
      percent: progress.percent,
      phase: progress.percent >= 100 ? 'processing' : 'uploading',
    });
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const openEditModal = (version) => {
    setFormValues({
      package_name: version.package_name || '',
      description: version.description || '',
      type: version.type || 'subsystem',
      file: null,
    });
    setCurrentVersionId(version.id);
    setCurrentUrl(version.url || '');
    setIsEditing(true);
    setModalOpen(true);
  };

  const handleChange = (field) => (event) => {
    const value = field === 'file' ? event.target.files?.[0] ?? null : event.target.value;
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortColumn(column);
    setSortDirection(column === 'publish_date' ? 'desc' : 'asc');
  };

  const sortIndicator = (column) => {
    if (sortColumn !== column) {
      return '';
    }
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formValues.package_name.trim() || !formValues.description.trim() || !formValues.type) {
      return;
    }

    if (!isEditing && !formValues.file) {
      return;
    }

    if (formValues.file && !isValidArchiveFile(formValues.file)) {
      alert('El archivo debe ser .tar.gz o .tgz');
      return;
    }

    setSaving(true);
    setUploadProgress(
      formValues.file
        ? { percent: 0, loaded: 0, total: formValues.file.size, phase: 'uploading' }
        : null
    );
    try {
      const hasFileUpload = Boolean(formValues.file);
      const response = isEditing
        ? await update_subsystem_version(
            token,
            currentVersionId,
            formValues.package_name.trim(),
            formValues.description.trim(),
            formValues.type,
            formValues.file,
            hasFileUpload ? handleUploadProgress : undefined
          )
        : await create_subsystem_version(
            token,
            formValues.package_name.trim(),
            formValues.description.trim(),
            formValues.type,
            formValues.file,
            handleUploadProgress
          );

      if (response === 'INVALID') {
        navigate('/');
        return;
      }

      if (response?.status === 'error') {
        alert(response.message || 'No se pudo guardar la versión.');
        return;
      }

      await fetchVersions();
      resetForm();
      setModalOpen(false);
    } catch (err) {
      alert(err?.message || 'Error al subir el archivo.');
    } finally {
      setSaving(false);
      setUploadProgress(null);
    }
  };

  const handleDelete = async (versionId) => {
    const confirmed = window.confirm('¿Eliminar esta versión del subsistema?');
    if (!confirmed) {
      return;
    }

    const response = await delete_subsystem_version(token, versionId);
    if (response === 'INVALID') {
      navigate('/');
      return;
    }
    if (response?.status === 'error') {
      alert(response.message || 'No se pudo eliminar la versión.');
      return;
    }
    await fetchVersions();
  };

  const handleCopyUrl = async (url) => {
    if (!url) {
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
    } catch (err) {
      console.error('No se pudo copiar la URL.', err);
    }
  };

  return (
    <Container fluid className="py-4">
      <Modal_proyectos
        isOpen={isModalOpen}
        onClose={() => {
          if (saving) {
            return;
          }
          setModalOpen(false);
          resetForm();
        }}
        title={isEditing ? 'Editar versión' : 'Agregar versión'}
      >
        <form onSubmit={handleSubmit} className="d-flex flex-column h-100">
          <div className="modal-inner" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {uploadProgress && (
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="small fw-semibold">
                    {uploadProgress.phase === 'processing'
                      ? 'Procesando en el servidor…'
                      : 'Subiendo archivo…'}
                  </span>
                  <span className="small text-muted">
                    {uploadProgress.phase === 'uploading'
                      ? `${uploadProgress.percent}% · ${formatBytes(uploadProgress.loaded)} / ${formatBytes(uploadProgress.total)}`
                      : 'Guardando en Google Cloud Storage'}
                  </span>
                </div>
                <ProgressBar
                  now={uploadProgress.phase === 'processing' ? 100 : uploadProgress.percent}
                  animated={uploadProgress.phase === 'processing'}
                  striped={uploadProgress.phase === 'processing'}
                  label={uploadProgress.phase === 'uploading' ? `${uploadProgress.percent}%` : undefined}
                />
              </div>
            )}
            <div className="mb-3">
              <label className="form-label" htmlFor="subsystem-package-name">
                Nombre de paquete
              </label>
              <input
                id="subsystem-package-name"
                type="text"
                className="form-control"
                value={formValues.package_name}
                onChange={handleChange('package_name')}
                maxLength={100}
                required
                disabled={saving}
              />
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="subsystem-type">
                Tipo de paquete
              </label>
              <select
                id="subsystem-type"
                className="form-select"
                value={formValues.type}
                onChange={handleChange('type')}
                required
                disabled={saving}
              >
                {PACKAGE_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="subsystem-description">
                Descripción
              </label>
              <textarea
                id="subsystem-description"
                className="form-control"
                rows={3}
                value={formValues.description}
                onChange={handleChange('description')}
                required
                disabled={saving}
              />
            </div>
            <div className="mb-3">
              <label className="form-label" htmlFor="subsystem-file">
                Archivo (.tar.gz)
              </label>
              <input
                id="subsystem-file"
                type="file"
                className="form-control"
                accept=".tar.gz,.tgz,application/gzip,application/x-gzip"
                onChange={handleChange('file')}
                required={!isEditing}
                disabled={saving}
              />
              {formValues.file && (
                <div className="form-text">
                  Tamaño seleccionado: {formatBytes(formValues.file.size)}
                </div>
              )}
              {isEditing && (
                <div className="form-text">
                  Deja vacío para mantener el archivo actual.
                </div>
              )}
            </div>
            {isEditing && currentUrl && (
              <div className="mb-3">
                <label className="form-label">URL actual</label>
                <input type="text" className="form-control" value={currentUrl} readOnly />
              </div>
            )}
          </div>
          <div className="modal-footer-fixed">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => {
                setModalOpen(false);
                resetForm();
              }}
              disabled={saving}
            >
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving
                ? uploadProgress?.phase === 'processing'
                  ? 'Procesando…'
                  : uploadProgress
                    ? `Subiendo… ${uploadProgress.percent}%`
                    : 'Guardando…'
                : isEditing
                  ? 'Guardar cambios'
                  : 'Agregar versión'}
            </button>
          </div>
        </form>
      </Modal_proyectos>

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <h2 className="mb-1">Subsistema TOMi</h2>
          <p className="text-muted mb-0">Administra las versiones del subsistema Debian.</p>
        </div>
        <button type="button" className="btn btn-primary" onClick={openCreateModal}>
          Agregar versión
        </button>
      </div>

      <div className="d-flex flex-wrap gap-3 mb-3">
        <InputGroup style={{ maxWidth: '320px' }}>
          <InputGroup.Text>
            <Search size={16} />
          </InputGroup.Text>
          <Form.Control
            type="search"
            placeholder="Filtrar por nombre…"
            value={nameFilter}
            onChange={(event) => setNameFilter(event.target.value)}
          />
        </InputGroup>
        <Form.Select
          style={{ maxWidth: '240px' }}
          value={typeFilter}
          onChange={(event) => setTypeFilter(event.target.value)}
          aria-label="Filtrar por tipo"
        >
          <option value="">Todos los tipos</option>
          {PACKAGE_TYPES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Form.Select>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="card shadow-sm">
        <div className="card-body p-0">
          <Table responsive hover className="mb-0 align-middle">
            <thead className="table-light">
              <tr>
                {Object.entries(SORTABLE_COLUMNS).map(([column, label]) => (
                  <th
                    key={column}
                    role="button"
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort(column)}
                    title={`Ordenar por ${label}`}
                  >
                    {label}
                    {sortIndicator(column)}
                  </th>
                ))}
                <th className="text-end">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    Cargando versiones…
                  </td>
                </tr>
              )}
              {!loading && filteredVersions.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-4">
                    No hay versiones registradas.
                  </td>
                </tr>
              )}
              {!loading &&
                filteredVersions.map((version) => (
                  <tr key={version.id}>
                    <td className="fw-semibold">{version.package_name}</td>
                    <td>{formatTypeLabel(version.type)}</td>
                    <td title={version.description || ''}>
                      {truncateDescription(version.description)}
                    </td>
                    <td>{formatPublishDate(version.publish_date)}</td>
                    <td>
                      {version.url ? (
                        <div className="d-flex align-items-center gap-2">
                          <a
                            href={version.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-truncate"
                            style={{ maxWidth: '220px' }}
                          >
                            {version.url}
                          </a>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => handleCopyUrl(version.url)}
                            aria-label="Copiar URL"
                            title="Copiar URL"
                          >
                            Copiar
                          </button>
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="text-end">
                      <div className="d-flex justify-content-end gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => openEditModal(version)}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDelete(version.id)}
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>
      </div>
    </Container>
  );
}

export default SubsistemaTOMi;
